// Building blocks shared by the browser voice code: device types, platform detection, a
// promise mutex and the bridge to the C# AudioLogger.

//#region Constants
export const DEVICE_CHANGE_DEBOUNCE_MS = 300;
// Suppresses auto-switching right after an explicit switch: on Android, Bluetooth HFP tears
// down and reconnects within ~1-2s of a switch, which would otherwise bounce the user back
// to the device they just left.
export const EXPLICIT_SWITCH_COOLDOWN_MS = 8000;
// How long an input without a paired output stays eligible for late pairing (Bluetooth
// headsets can enumerate their HFP mic seconds before their A2DP output appears).
export const UNPAIRED_INPUT_TTL_MS = 5000;
export const MIC_ACQUIRE_RETRY_DELAY_MS = 500;
// Lets the previous device release fully before re-acquiring on mobile.
export const MIC_ACQUIRE_SETTLE_DELAY_MS = 200;
// The browser's own voice processing for every capture: echo cancellation, noise suppression and gain control.
export const VOICE_CAPTURE_CONSTRAINTS: MediaTrackConstraints = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true
};
//#endregion

//#region Shared types
export type DeviceKind = 'audioinput' | 'audiooutput';

// Matches the C# AudioDeviceInfo DTO (camelCase JSON contract).
export interface DeviceLite {
    deviceId: string;
    groupId: string;
    kind: DeviceKind;
    label: string;
}

export interface DeviceSnapshot {
    inputs: DeviceLite[];   // every audioinput entry, including the "default"/"communications" virtual aliases
    outputs: DeviceLite[];
    defaultInputLabel: string;
    defaultOutputLabel: string;
}
//#endregion

//#region Pure helpers
export function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function nowMs(): number {
    return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
}

export function isMobilePlatform(): boolean {
    return /android|iphone|ipad|ipod/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '');
}

export function isAndroid(): boolean {
    return /android/i.test(typeof navigator !== 'undefined' ? navigator.userAgent : '');
}

// iOS Safari (not Chrome-on-iOS / Firefox-on-iOS / Edge-on-iOS) needs the audioSession workaround.
export function isIOSSafari(): boolean {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const isIOS = /iphone|ipad|ipod/i.test(ua);
    const isSafari = /safari/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
    return isIOS && isSafari;
}

// Narrower than isHeadsetLabel on purpose: this drives the iOS audioSession lock strategy,
// where only true headset/Bluetooth mics need the 'play-and-record' route lock.
export function isHeadsetTrack(track: MediaStreamTrack): boolean {
    return /headset|bluetooth|airpod|wireless|hands.?free/i.test(track.label);
}

// Mirrors AudioDeviceHelper.IsHeadsetLabel (C#) — keep the keyword lists in sync.
export function isHeadsetLabel(label: string): boolean {
    return /headset|bluetooth|airpod|wireless|hands.?free|headphone|earphone|wired/i.test(label || '');
}

// Narrow on purpose: identifies Bluetooth hands-free (HFP) endpoints — "Headset (AirPods)",
// "Hands-Free AG Audio" — as opposed to their A2DP "Headphones (...)" siblings.
export function isHandsFreeLabel(label: string): boolean {
    return /headset|hands.?free/i.test(label || '');
}

// "default"/"communications" are virtual aliases; an empty id means permission is not granted.
export function isRealDeviceId(deviceId: string): boolean {
    return !!deviceId && deviceId !== 'default' && deviceId !== 'communications';
}

export function stripDefaultPrefix(label: string): string {
    return label.replace(/^default\s*-\s*/i, '').trim();
}

// Mirrors AudioDeviceHelper.NormalizeLabel (C#) — keep behaviorally identical.
export function normalizeLabel(label: string | undefined): string {
    if (!label) return '';
    let l = label.toLowerCase().trim();
    l = l.replace(/^default\s*-\s*/i, '');
    l = l.replace(/^communications\s*-\s*/i, '');
    const parenMatch = l.match(/\(([^()]+(?:\([^()]*\)[^()]*)*)\)\s*$/);
    if (parenMatch) l = parenMatch[1];
    return l.replace(/\s+/g, ' ').trim();
}

export function emptyDeviceSnapshot(): DeviceSnapshot {
    return { inputs: [], outputs: [], defaultInputLabel: '', defaultOutputLabel: '' };
}
//#endregion

//#region PromiseMutex
export class PromiseMutex {
    private locked = false;
    private waiting: (() => void)[] = [];

    async lock(): Promise<void> {
        if (!this.locked) {
            this.locked = true;
            return;
        }
        await new Promise<void>(resolve => this.waiting.push(resolve));
    }

    unlock(): void {
        if (this.waiting.length > 0) {
            const next = this.waiting.shift();
            if (next) next();
        } else {
            this.locked = false;
        }
    }
}
//#endregion

//#region .NET logger bridge
// Null-safe bridge to the C# AudioLogger DotNetObjectReference. The only place that knows
// the Log*Async method names. All interop failures are swallowed — logging must never break audio.
export class LoggerBridge {
    private ref: any = null;

    setRef(ref: any): void {
        if (ref) this.ref = ref;
    }

    async info(message: string, properties?: Record<string, any>): Promise<void> {
        await this.invoke('LogInfoAsync', message, properties || {});
    }

    async warning(message: string, properties?: Record<string, any>): Promise<void> {
        await this.invoke('LogWarningAsync', message, properties || {});
    }

    async error(message: string, exceptionDetails?: string, properties?: Record<string, any>): Promise<void> {
        await this.invoke('LogErrorAsync', message, exceptionDetails || null, properties || {});
    }

    async metric(name: string, value: number, properties?: Record<string, any>): Promise<void> {
        await this.invoke('LogMetricAsync', name, value, properties || {});
    }

    private async invoke(method: string, ...args: any[]): Promise<void> {
        if (!this.ref) return;
        try {
            await this.ref.invokeMethodAsync(method, ...args);
        } catch { }
    }
}
//#endregion
